import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { gstAPI } from '../../services/api';
import { useGst } from './GstContext';
import { GST_RATES, msg, useStates } from './gstUi';

const CONFIG = {
  parties: {
    title: 'Parties', single: 'party', list: (p) => gstAPI.parties(p), save: (id, b) => gstAPI.saveParty(id, b), remove: (id) => gstAPI.removeParty(id),
    columns: [['name', 'Name'], ['gstin', 'GSTIN'], ['phone', 'Phone']],
    empty: { name: '', gstin: '', address: '', phone: '', email: '' },
    fields: [['name', 'Name *', 'text'], ['gstin', 'GSTIN (leave blank if unregistered)', 'text'], ['address', 'Address', 'text'], ['phone', 'Phone', 'text'], ['email', 'Email', 'text']],
  },
  items: {
    title: 'Items', single: 'item', list: (p) => gstAPI.items(p), save: (id, b) => gstAPI.saveItem(id, b), remove: (id) => gstAPI.removeItem(id),
    columns: [['name', 'Name'], ['gstPercent', 'GST %'], ['unit', 'Unit'], ['defaultRate', 'Default rate']],
    empty: { name: '', gstPercent: 18, unit: 'NA', defaultRate: 0 },
    fields: [['name', 'Name *', 'text'], ['gstPercent', 'GST rate *', 'rate'], ['unit', 'Unit (NA for services)', 'text'], ['defaultRate', 'Amount (incl. GST)', 'number']],
  },
};

export default function GstMasterTabs({ kind }) {
  const cfg = CONFIG[kind];
  const { call } = useGst();
  const states = useStates();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [form, setForm] = useState(null); // null = closed; object = editing/new (with optional _id)
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    call(() => cfg.list(q ? { q } : {})).then((b) => setRows(b.data)).catch((e) => toast.error(msg(e)));
  }, [call, cfg, q]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { _id, ...body } = form;
      await call(() => cfg.save(_id, body));
      toast.success(`${cfg.single} saved`); setForm(null); load();
    } catch (err) { toast.error(msg(err)); } finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Deactivate "${r.name}"? Existing invoices keep their copy.`)) return;
    try { await call(() => cfg.remove(r._id)); load(); } catch (err) { toast.error(msg(err)); }
  };

  const input = ([key, label, type]) => (
    <div className="form-group" key={key}>
      <label className="form-label">{label}</label>
      {type === 'state' ? (
        <select className="form-control" value={form[key] || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })}>
          <option value="">Select…</option>{states.map((s) => <option key={s.code} value={s.code}>{s.code} – {s.name}</option>)}
        </select>
      ) : type === 'rate' ? (
        <select className="form-control" value={form[key]} onChange={(e) => setForm({ ...form, [key]: Number(e.target.value) })}>
          {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
        </select>
      ) : (
        <input className="form-control" type={type === 'number' ? 'number' : 'text'} step={type === 'number' ? '0.01' : undefined}
          value={form[key] ?? ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
      )}
    </div>
  );

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input className="form-control" style={{ maxWidth: 260 }} placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn btn-primary" onClick={() => setForm({ ...cfg.empty })}><Plus size={14} /> New {cfg.single}</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{cfg.columns.map(([, l]) => <th key={l} style={{ textAlign: 'left', padding: 6 }}>{l}</th>)}<th /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._id} style={{ borderTop: '1px solid rgba(128,128,128,.25)' }}>
                {cfg.columns.map(([k]) => <td key={k} style={{ padding: 6 }}>{r[k] ?? ''}</td>)}
                <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                  <button className="btn btn-secondary" onClick={() => setForm({ ...cfg.empty, ...r })}><Pencil size={14} /></button>{' '}
                  <button className="btn btn-secondary" onClick={() => remove(r)}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={cfg.columns.length + 1} style={{ padding: 12, opacity: 0.6 }}>Nothing here yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {form && (
        <div className="modal-overlay">
          <form className="modal" onSubmit={submit}>
            <div className="modal-header"><h3>{form._id ? 'Edit' : 'New'} {cfg.single}</h3><button type="button" className="modal-close" onClick={() => setForm(null)}>×</button></div>
            <div className="modal-body">{cfg.fields.map(input)}</div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setForm(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
